import { useRef, useState } from 'react';
import { Pin, PinOff, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTilePins } from '@/hooks/useTilePins';

/**
 * Mobile-only 3-column tile grid for dashboard navigation.
 *
 * Props:
 *  - tiles: array of tile configs. Each tile:
 *      { key, label, hint?, icon, iconBg?, iconColor?, badge?, badgeColor?,
 *        onClick, active? }
 *  - storageScope: string key for persisting pins (per-role, per-dashboard)
 *  - defaultPins: array of tile keys that are pinned by default
 *  - onBack?: optional back-to-menu callback
 *  - showBackPill: whether to show "Back to menu" pill
 */
const BADGE_COLORS = {
  red:   'bg-red-500 text-white',
  amber: 'bg-amber-500 text-white',
  green: 'bg-emerald-500 text-white',
  blue:  'bg-blue-500 text-white',
  gray:  'bg-gray-400 text-white',
};

const LONG_PRESS_MS = 500;
// Tap-tuning constants to make the grid less twitchy on phones.
//   * MOVE_SLOP_PX  — if the finger drifts more than this between touchstart
//     and touchend, treat the gesture as a scroll, not a tap.
//   * MIN_TAP_MS    — releases under this many ms are usually accidental flicks
//     during a scroll; ignored.
//   * REPEAT_LOCK_MS — short window after a successful tap during which any
//     further tap on the same tile is ignored (double-firing on shaky hands).
const MOVE_SLOP_PX = 12;
const MIN_TAP_MS = 80;
const REPEAT_LOCK_MS = 600;

export function MobileTileGrid({
  tiles,
  storageScope,
  defaultPins,
  onBack,
  showBackPill = false,
}) {
  const { pins, toggle, reset } = useTilePins(storageScope, defaultPins, 6);
  const [actionTile, setActionTile] = useState(null); // tile currently under long-press menu
  const timerRef = useRef(null);
  const pressedRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0, t: 0, cancelled: false });
  const lastFireRef = useRef({ key: null, t: 0 });

  const sortedTiles = [...tiles].sort((a, b) => {
    const aPinned = pins.includes(a.key);
    const bPinned = pins.includes(b.key);
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    if (aPinned && bPinned) return pins.indexOf(a.key) - pins.indexOf(b.key);
    return 0; // preserve original order for un-pinned
  });

  const startLongPress = (tile, ev) => {
    pressedRef.current = false;
    startRef.current = {
      x: ev?.touches?.[0]?.clientX ?? ev?.clientX ?? 0,
      y: ev?.touches?.[0]?.clientY ?? ev?.clientY ?? 0,
      t: Date.now(),
      cancelled: false,
    };
    timerRef.current = setTimeout(() => {
      if (startRef.current.cancelled) return;
      pressedRef.current = true;
      setActionTile(tile);
      // Haptic feedback on supporting browsers
      if (window.navigator?.vibrate) window.navigator.vibrate(15);
    }, LONG_PRESS_MS);
  };

  // Called on every touchmove. If the finger drifts past MOVE_SLOP_PX the
  // user is scrolling, not tapping — abort both the long-press timer and
  // the eventual touchend tap.
  const handleMove = (ev) => {
    if (startRef.current.cancelled) return;
    const t = ev.touches?.[0];
    if (!t) return;
    const dx = Math.abs(t.clientX - startRef.current.x);
    const dy = Math.abs(t.clientY - startRef.current.y);
    if (dx > MOVE_SLOP_PX || dy > MOVE_SLOP_PX) {
      startRef.current.cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    }
  };

  const endLongPress = (tile, fire) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const elapsed = Date.now() - startRef.current.t;
    const wasCancelledByScroll = startRef.current.cancelled;

    if (!pressedRef.current && fire && !wasCancelledByScroll && elapsed >= MIN_TAP_MS) {
      // Suppress duplicate fires on the same tile within REPEAT_LOCK_MS — the
      // user's finger is shakier than the OS double-tap suppression handles.
      const now = Date.now();
      if (lastFireRef.current.key === tile.key && now - lastFireRef.current.t < REPEAT_LOCK_MS) {
        // ignored
      } else {
        lastFireRef.current = { key: tile.key, t: now };
        tile.onClick?.();
      }
    }
    pressedRef.current = false;
    startRef.current.cancelled = false;
  };

  const cancelLongPress = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    pressedRef.current = false;
  };

  return (
    <>
      {showBackPill && onBack && (
        <div className="flex justify-end px-3 pt-3" data-testid="mobile-grid-back">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 text-sm font-semibold rounded-full border border-purple-200 active:scale-95 transition-transform"
          >
            <X className="w-3.5 h-3.5" /> Back to menu
          </button>
        </div>
      )}

      <div
        className="grid grid-cols-3 gap-2 px-3 py-3"
        data-testid="mobile-tile-grid"
      >
        {sortedTiles.map((tile) => {
          const isPinned = pins.includes(tile.key);
          const badgeCls = BADGE_COLORS[tile.badgeColor || 'red'];
          return (
            <button
              key={tile.key}
              type="button"
              data-testid={`tile-${tile.key}`}
              onContextMenu={(e) => e.preventDefault()}
              onTouchStart={(e) => startLongPress(tile, e)}
              onTouchMove={handleMove}
              onTouchEnd={() => endLongPress(tile, true)}
              onTouchCancel={cancelLongPress}
              onMouseDown={(e) => startLongPress(tile, e)}
              onMouseUp={() => endLongPress(tile, true)}
              onMouseLeave={cancelLongPress}
              className={`relative bg-white rounded-xl border-2 p-2.5 min-h-[108px] flex flex-col items-center justify-center text-center active:scale-95 transition-transform ${
                tile.active
                  ? 'border-purple-600 bg-gradient-to-br from-purple-50 to-violet-50 shadow-md'
                  : 'border-gray-200'
              }`}
            >
              {tile.badge !== undefined && tile.badge !== null && tile.badge !== '' && (
                <span
                  className={`absolute top-1.5 right-1.5 ${badgeCls} text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1`}
                  data-testid={`tile-badge-${tile.key}`}
                >
                  {tile.badge}
                </span>
              )}
              {isPinned && (
                <Pin
                  className="absolute top-1.5 left-1.5 w-3 h-3 text-purple-500"
                  fill="currentColor"
                />
              )}
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center mb-1.5"
                style={{
                  backgroundColor: tile.iconBg || '#ede9fe',
                  color: tile.iconColor || '#6d28d9',
                }}
              >
                {tile.icon}
              </div>
              <div className="text-[11px] font-semibold leading-tight text-gray-900">
                {tile.label}
              </div>
              {tile.hint && (
                <div className="text-[9px] text-gray-500 mt-0.5 leading-tight">
                  {tile.hint}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Long-press action sheet */}
      {actionTile && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center sm:justify-center"
          onClick={() => setActionTile(null)}
          data-testid="tile-action-sheet"
        >
          <div
            className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3 pb-3 border-b">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{
                  backgroundColor: actionTile.iconBg || '#ede9fe',
                  color: actionTile.iconColor || '#6d28d9',
                }}
              >
                {actionTile.icon}
              </div>
              <div>
                <div className="font-semibold text-gray-900">{actionTile.label}</div>
                {actionTile.hint && (
                  <div className="text-xs text-gray-500">{actionTile.hint}</div>
                )}
              </div>
            </div>

            <button
              className="w-full flex items-center gap-3 px-3 py-3 hover:bg-gray-50 rounded-lg text-left"
              onClick={() => {
                const isPinned = pins.includes(actionTile.key);
                if (!isPinned && pins.length >= 6) {
                  toast.error('You can pin up to 6 tiles. Unpin one first.');
                  setActionTile(null);
                  return;
                }
                toggle(actionTile.key);
                toast.success(isPinned ? 'Unpinned' : 'Pinned to top');
                setActionTile(null);
              }}
              data-testid="tile-action-pin"
            >
              {pins.includes(actionTile.key) ? (
                <>
                  <PinOff className="w-5 h-5 text-purple-600" />
                  <span className="font-medium">Unpin from top</span>
                </>
              ) : (
                <>
                  <Pin className="w-5 h-5 text-purple-600" />
                  <span className="font-medium">Pin to top</span>
                </>
              )}
            </button>

            <button
              className="w-full flex items-center gap-3 px-3 py-3 hover:bg-gray-50 rounded-lg text-left"
              onClick={() => {
                if (window.confirm('Reset menu order to default?')) {
                  reset();
                  toast.success('Menu reset to default');
                }
                setActionTile(null);
              }}
              data-testid="tile-action-reset"
            >
              <RotateCcw className="w-5 h-5 text-gray-600" />
              <span className="font-medium">Reset menu to default</span>
            </button>

            <button
              className="w-full mt-2 px-3 py-2 text-sm text-gray-500"
              onClick={() => setActionTile(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default MobileTileGrid;
